import { getPropertyComAuData } from '../components/PropertySearch/utils'
import { isRiskUnavailable } from './riskStatus'
import { formatPowerlineResult } from './powerlineStatus'
import { formatProximityResult } from './proximityStatus'
import { formatHousingResult } from './housingStatus'
import { normalizeInsuranceEstimate } from './insuranceStatus'

const activeStatus = /^(pending|checking|loading|processing|running)$/i
const failedStatus = /unknown|unavailable|not available|error|failed|timeout|not researched/i
const hasFailure = raw => Boolean(raw?.error || raw?.success === false || failedStatus.test(String(raw?.state || raw?.status || ''))
  || /error|failed|unavailable|not available|timeout|timed out|not configured|unable to|could not/i.test(String(raw?.details || raw?.message || '')))
const resolved = check => ['clear', 'flagged'].includes(check.state)
const physical = check => ['hazard', 'infrastructure'].includes(check.category)

function unresolvedState(raw, loading) {
  const status = String(raw?.state || raw?.status || '').trim()
  if (activeStatus.test(status)) return 'pending'
  if (hasFailure(raw)) return 'unknown'
  return loading ? 'pending' : 'unknown'
}

function check(id, label, category, state, detail, action) {
  return { id, label, category, state, detail, action, mandatory: true,
    severity: state === 'flagged' ? category === 'area_context' ? 'context' : category === 'hazard' ? 'high' : 'review'
      : state === 'unknown' ? 'missing' : state === 'pending' ? 'pending' : 'none' }
}

export function assessHazardState(risk, { loading = false } = {}) {
  const raw = { ...risk, details: risk?.details || risk?.reasoning }
  const status = String(raw.state || raw.status || '').toLowerCase().trim()
  const inconsistent = raw.detected === false && ['detected', 'warning'].includes(status)
    || raw.detected === true && ['safe', 'not detected', 'clear'].includes(status)
  const explicitlyUnresolved = activeStatus.test(status) || hasFailure(raw)
  return explicitlyUnresolved || isRiskUnavailable(raw) || inconsistent ? unresolvedState(raw, loading)
    : raw.detected === true ? 'flagged' : 'clear'
}

function hazardCheck(dd, id, label, action, loading) {
  const state = assessHazardState({ detected: dd?.[`${id}_detected`], status: dd?.[`${id}_status`],
    details: dd?.[`${id}_details`] || dd?.[`${id}_reasoning`], error: dd?.[`${id}_error`],
    success: dd?.[`${id}_success`] }, { loading })
  return check(id, label, 'hazard', state,
    state === 'flagged' ? `${label} was detected.`
      : state === 'clear' ? `${label} was checked and not detected.`
        : state === 'pending' ? `${label} check is in progress.` : `${label} could not be confirmed.`,
    state === 'pending' ? 'Wait for this check to finish.' : action)
}

function proximityCheck(raw, id, label, formatted, action, loading) {
  const explicit = String(raw?.state || raw?.status || '').trim()
  const state = hasFailure(raw) || activeStatus.test(explicit) ? unresolvedState(raw, loading)
    : formatted.status === 'Detected' ? 'flagged' : formatted.status === 'Not Detected' ? 'clear' : unresolvedState(raw, loading)
  const distance = Number.isFinite(raw?.distance_meters) ? ` ${Math.round(raw.distance_meters)} m away` : ''
  const detail = state === 'flagged' ? `${label} was detected${distance}.`
    : state === 'clear' ? `${label} was not detected within the checked distance.`
      : state === 'pending' ? `${label} check is in progress.` : `${label} could not be confirmed.`
  return check(id, label, 'infrastructure', state, detail,
    state === 'pending' ? 'Wait for this check to finish.' : action)
}

const positiveMoney = value => {
  if (typeof value === 'number') return Number.isFinite(value) && value > 0 ? value : null
  if (typeof value !== 'string') return null
  const match = value.trim().match(/^\$?\s*(\d[\d,]*(?:\.\d+)?)\s*([km])?$/i)
  if (!match) return null
  const number = Number(match[1].replace(/,/g, '')) * ({ k: 1000, m: 1000000 }[match[2]?.toLowerCase()] || 1)
  return Number.isFinite(number) && number > 0 ? number : null
}

// Insurance evidence stays in the ledger and guidance but does not decide BUY for now
// (Amar, 28 September 2026). Local premium research does not yet return supported
// local prices, so gating on it made BUY unreachable. Set true to restore the gate.
export const INSURANCE_DECIDES_VERDICT = false

/** One evidence assessment shared by the cover, verdict, guidance and ledger. */
export function assessDueDiligence(results, { loading = false, insuranceDecidesVerdict = INSURANCE_DECIDES_VERDICT } = {}) {
  const dd = results?.propertyFeatures
  const pca = getPropertyComAuData(results?.property_com_au)
  const confidence = String(dd?.estimate_confidence || pca?.property_value_confidence || '').trim()
    .match(/^(high|medium|low)(?:\s+confidence)?$/i)?.[1]?.toLowerCase() || 'unknown'
  const value = positiveMoney(dd?.estimate_mid) ?? positiveMoney(pca?.property_value_main)
  const valuationSupported = value != null && ['high', 'medium'].includes(confidence)
  const valuationState = valuationSupported ? 'clear' : loading && value == null ? 'pending' : 'unknown'
  const checks = [check('valuation', 'Property valuation', 'valuation', valuationState,
    valuationSupported ? 'A property valuation is available with stated confidence.'
      : valuationState === 'pending' ? 'Property valuation is in progress.' : 'A sufficiently supported property valuation is missing.',
    'Review the property valuation against recent comparable sales.'),
  ...[
    ['flood', 'Flood overlay', 'Check the official flood map and obtain property insurance terms.'],
    ['bushfire', 'Bushfire zone', 'Confirm the bushfire rating and any building or insurance requirements.'],
    ['heritage', 'Heritage listing', 'Confirm the listing and restrictions with the local council.'],
    ['flight_path', 'Flight-path exposure', 'Check official aircraft noise information and inspect at different times.'],
  ].map(([id, label, action]) => hazardCheck(dd, id, label, action, loading)),
  proximityCheck(dd?.powerline_proximity, 'powerline', 'Powerline proximity', formatPowerlineResult(dd?.powerline_proximity),
    'Confirm the line location and any easement with the electricity network.', loading),
  proximityCheck(dd?.main_road_proximity, 'main_road', 'Main road proximity', formatProximityResult(dd?.main_road_proximity, 'road'),
    'Inspect traffic, noise and driveway access at busy times.', loading),
  proximityCheck(dd?.train_line_proximity, 'train', 'Train station proximity', formatProximityResult(dd?.train_line_proximity, 'train'),
    'Check the nearby rail infrastructure and inspect noise at different times.', loading)]

  const housing = formatHousingResult(dd?.housing_commission)
  const housingSupported = housing.status !== 'Unknown' && typeof housing.public_rate === 'number'
  const housingExplicit = String(dd?.housing_commission?.state || dd?.housing_commission?.status || '').trim()
  const housingState = activeStatus.test(housingExplicit) || hasFailure(dd?.housing_commission) ? unresolvedState(dd?.housing_commission, loading)
    : housingSupported ? housing.status === 'Detected' ? 'flagged' : 'clear' : unresolvedState(dd?.housing_commission, loading)
  const area = housing.pocket_level ? 'census area' : 'suburb area'
  checks.push(check('housing_area', 'Public housing area data', 'area_context', housingState,
    housingSupported && ['clear', 'flagged'].includes(housingState) ? `${housing.public_rate}% public housing in the ${area}. This does not identify this property or individual homes.`
      : housingState === 'pending' ? 'Public housing area data is being checked.' : 'Supported public housing area data is missing.',
    housingState === 'pending' ? 'Wait for the area data check to finish.' : 'Review the published area coverage and inspect the surrounding streets.'))

  const insurance = normalizeInsuranceEstimate(dd?.insurance_estimate)
  for (const [profile, label] of [['owner_occupier', 'Home and contents insurance'], ['investment', 'Building and landlord insurance']]) {
    const data = insurance.profiles[profile]
    const state = data.state === 'pending' || dd?.insurance_estimate?.profiles?.[profile]?.state === 'pending' ? 'pending' : data.state === 'available' ? data.status === 'Higher' ? 'flagged' : 'clear'
      : !dd?.insurance_estimate && loading ? 'pending' : 'unknown'
    checks.push(check(`insurance_${profile}`, label, 'insurance', state,
      state === 'clear' ? `${label} has local published cost evidence. An insurer quote is still required for this property.`
        : state === 'flagged' ? `${label} is above its supported comparison benchmark.`
          : state === 'pending' ? `${label} research is in progress.` : `No supported local estimate is available for ${label.toLowerCase()}.`,
      state === 'pending' ? 'Wait for this cover research to finish.' : `Request an insurer quote for ${label.toLowerCase()}, using the replacement value, excess and property details.`))
  }

  const flags = checks.filter(item => physical(item) && item.state === 'flagged')
  const contextFindings = checks.filter(item => item.category === 'area_context' && item.state === 'flagged')
  const majorFlagCount = flags.filter(item => ['flood', 'bushfire', 'heritage'].includes(item.id)).length
  const missingCount = checks.filter(item => item.state === 'unknown').length
  const pendingCount = checks.filter(item => item.state === 'pending').length
  const reviewCount = checks.filter(item => item.state === 'flagged').length
  const running = loading || pendingCount > 0
  const decisive = checks.filter(item => insuranceDecidesVerdict || item.category !== 'insurance')
  const decisiveReviewCount = decisive.filter(item => item.state === 'flagged').length
  // Deciding checks with no answer yet (evidence missing, or still running):
  // while any remains, the verdict cannot be BUY.
  const unresolvedDecisiveIds = decisive.filter(item => !resolved(item)).map(item => item.id)
  const insuranceOpen = checks.some(item => item.category === 'insurance' && item.state !== 'clear')
  const verdict = !dd ? null : majorFlagCount >= 2 ? 'WALK'
    : decisive.every(item => item.state === 'clear') && !loading ? 'BUY' : 'INVESTIGATE'
  const qualifier = majorFlagCount >= 2 ? 'Multiple major risks'
    : decisiveReviewCount ? `${decisiveReviewCount} ${decisiveReviewCount === 1 ? 'finding needs' : 'findings need'} review`
      : running ? 'Research in progress'
        : decisive.some(item => item.state === 'unknown') ? 'Checks incomplete'
          : insuranceOpen ? 'Insurance quotes needed' : 'Checks reviewed'
  const missingInsurance = checks.filter(item => item.category === 'insurance' && item.state === 'unknown')
  const otherMissing = checks.filter(item => item.category !== 'insurance' && item.state === 'unknown')
  const pending = checks.filter(item => item.state === 'pending')
  const summary = [
    verdict === 'BUY' ? 'The required checks are supported with no flagged findings.' : '',
    ...checks.filter(item => item.state === 'flagged').map(item => item.detail),
    missingInsurance.length ? `Local insurance estimates are missing for ${missingInsurance.map(item => item.label.toLowerCase().replace(' insurance', '')).join(' and ')} cover.` : '',
    otherMissing.length ? `${otherMissing.map(item => item.label).join(', ')} ${otherMissing.length === 1 ? 'still needs' : 'still need'} evidence.` : '',
    pending.length ? `Research is in progress for ${pending.map(item => item.label.toLowerCase()).join(', ')}.` : '',
    missingInsurance.length ? reviewCount || otherMissing.length ? 'Obtain insurer quotes and review the findings before committing.' : 'Obtain insurer quotes before committing.'
      : reviewCount || missingCount ? 'Review these findings before committing.' : '',
    verdict === 'BUY' && !missingInsurance.length ? 'Confirm property insurance terms before committing.' : '',
  ].filter(Boolean).join(' ') || (running ? 'Research is still in progress.' : 'Review the findings before proceeding.')
  const findingOrder = { flagged: 0, unknown: 1, pending: 2 }
  const findings = checks.filter(item => item.state !== 'clear').sort((a, b) => findingOrder[a.state] - findingOrder[b.state])
  return { verdict, qualifier, summary, checks, findings,
    flags, flagCount: flags.length, contextFindings, contextCount: contextFindings.length, majorFlagCount, reviewCount,
    missingCount, pendingCount, unresolvedCount: missingCount + pendingCount, unresolvedDecisiveIds,
    checkedCount: checks.filter(resolved).length, totalChecks: checks.length, running,
    valuationConfidence: { level: confidence === 'unknown' ? 'Unknown' : confidence[0].toUpperCase() + confidence.slice(1),
      supported: valuationSupported, scope: 'valuation', label: 'Valuation confidence' } }
}
